
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Home() {
  useEffect(() => {
    // Schema.org JSON-LD for Organization and LocalBusiness
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "訪問介護ゆきのケア",
      "alternateName": "株式会社歩",
      "description": "神奈川県川崎市の訪問介護サービス。身体介護・生活援助を通じて、ご利用者さまの在宅生活を支援いたします。",
      "url": `${import.meta.env.VITE_SITE_URL}/`,
      "telephone": "044-280-7266",
      "faxNumber": "044-280-7267",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "戸手本町1-123-12 ドルフ5 203",
        "addressLocality": "川崎市幸区",
        "addressRegion": "神奈川県",
        "postalCode": "212-0023",
        "addressCountry": "JP"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 35.5494,
        "longitude": 139.6917
      },
      "areaServed": [
        {
          "@type": "City",
          "name": "川崎市幸区"
        },
        {
          "@type": "City",
          "name": "川崎市川崎区"
        },
        {
          "@type": "City",
          "name": "横浜市鶴見区"
        }
      ],
      "serviceType": ["身体介護", "生活援助", "訪問介護"],
      "openingHours": "Mo-Su 08:00-18:00",
      "foundingDate": "2017-02-01",
      "founder": {
        "@type": "Person",
        "name": "吉田 香"
      },
      "employee": {
        "@type": "Person",
        "name": "菊池 雪野",
        "jobTitle": "管理者"
      }
    };

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "川崎市訪問介護ゆきのケア - やさしさで、あなたの暮らしを支えます",
      "description": "神奈川県川崎市の訪問介護サービス「ゆきのケア」。身体介護・生活援助を通じて、ご利用者さまの在宅生活を支援いたします。",
      "url": `${import.meta.env.VITE_SITE_URL}/`,
      "mainEntity": organizationSchema
    };

    // Add schema to head
    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.textContent = JSON.stringify(organizationSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(script2);

    return () => {
      // Cleanup
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <h1>
                <img 
                  src="https://static.readdy.ai/image/4e28e17ba4e737ad2b4d0213d4252ea8/96aaf8d56f57e04898441455e82ac9b1.png" 
                  alt="訪問介護ゆきのケア" 
                  title="訪問介護ゆきのケア 川崎市"
                  className="h-16 w-auto object-contain"
                />
              </h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium">ホーム</Link>
              <Link to="/services" className="text-gray-700 hover:text-blue-600 font-medium">サービス内容</Link>
              <Link to="/careers" className="text-gray-700 hover:text-blue-600 font-medium">採用情報</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-end justify-center bg-cover bg-center pb-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://public.readdy.ai/ai/img_res/6065389bece7dd88075d474b3ca186bb.jpg')`
        }}
      >
        {/* ロゴを左上に配置 */}
        <div className="absolute top-8 left-8 z-10">
          <img 
            src="https://static.readdy.ai/image/4e28e17ba4e737ad2b4d0213d4252ea8/e0ffe532fa9e1db188a1f3e6c4219011.png" 
            alt="訪問介護ゆきのケア" 
            className="h-20 w-auto object-contain drop-shadow-lg"
          />
        </div>
        
        <div className="text-center text-white px-4 max-w-4xl">
          <p className="text-2xl md:text-3xl mb-8 font-medium">
            やさしさで、あなたの暮らしを支えます。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/services" 
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              サービス内容を見る
            </Link>
            <Link 
              to="/careers" 
              className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              採用情報を見る
            </Link>
          </div>
        </div>
      </section>

      {/* 企業理念セクション */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            私たちの理念
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start space-x-4 p-6 bg-blue-50 rounded-lg">
                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
                <p className="text-gray-700 leading-relaxed">
                  ご利用者やご家族の希望に寄り添い、やさしさのこもったサービス提供
                </p>
              </div>
              <div className="flex items-start space-x-4 p-6 bg-green-50 rounded-lg">
                <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
                <p className="text-gray-700 leading-relaxed">
                  在宅サービスを通じた社会貢献・地域社会に役立つ人材の育成
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4 p-6 bg-orange-50 rounded-lg">
                <div className="bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">3</div>
                <p className="text-gray-700 leading-relaxed">
                  全スタッフ・家族の幸福の追求
                </p>
              </div>
              <div className="flex items-start space-x-4 p-6 bg-pink-50 rounded-lg">
                <div className="bg-pink-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">4</div>
                <p className="text-gray-700 leading-relaxed">
                  障がいや高齢の方の機能を生かし、在宅生活を支える
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* サービス紹介セクション */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-8" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            提供しているサービス
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-4xl mx-auto">
            「訪問介護ゆきのケア」では、ご利用者さまの生活を支える2つのサービスを提供しています。
          </p>
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">身体介護</h3>
              <p className="text-gray-600 leading-relaxed">
                入浴・排泄・食事などの身体ケアを専門スタッフが丁寧に行います。ご利用者さまの尊厳を大切にしながら、安全で快適な介護を提供いたします。
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">生活援助</h3>
              <p className="text-gray-600 leading-relaxed">
                掃除・洗濯・調理などの家事支援を通じて、ご利用者さまの日常生活をサポートします。住み慣れたご自宅での生活を継続できるよう支援いたします。
              </p>
            </div>
          </div>
          <div className="text-center">
            <Link 
              to="/services" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              詳しく見る
            </Link>
          </div>
        </div>
      </section>

      {/* 対応エリアと時間 */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            ご利用可能エリアと提供時間
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">対応エリア</h3>
              <div className="space-y-2 text-lg text-gray-600">
                <p>川崎市幸区</p>
                <p>川崎市川崎区</p>
                <p>横浜市鶴見区</p>
              </div>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">サービス時間</h3>
              <p className="text-lg text-gray-600 mb-2">8:00～18:00</p>
              <p className="text-sm text-gray-500">（時間外はご相談ください）</p>
            </div>
          </div>
        </div>
      </section>

      {/* スタッフ募集案内 */}
      <section className="py-20 bg-gradient-to-r from-orange-50 to-pink-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-8" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            一緒に働きませんか？
          </h2>
          <p className="text-2xl text-center text-gray-600 mb-12">
            あなたの笑顔が、地域の安心につながります。
          </p>
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <img 
                src="https://readdy.ai/api/search-image?query=Group%20of%20Japanese%20home%20care%20workers%20in%20comfortable%20casual%20work%20clothes%20smiling%20naturally%20together%2C%20diverse%20team%20of%20caregivers%20in%20everyday%20clothing%20like%20polo%20shirts%20and%20comfortable%20pants%2C%20warm%20office%20environment%20with%20natural%20lighting%2C%20authentic%20workspace%20atmosphere%20showing%20genuine%20teamwork%20and%20friendship%2C%20realistic%20photography%20with%20natural%20expressions&width=800&height=400&seq=staff4&orientation=landscape" 
                alt="スタッフ募集" 
                className="w-full h-64 object-cover rounded-lg mb-6"
              />
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div className="p-4">
                  <i className="ri-user-heart-line text-3xl text-orange-500 mb-2"></i>
                  <p className="font-semibold">未経験者・ブランクOK</p>
                </div>
                <div className="p-4">
                  <i className="ri-calendar-check-line text-3xl text-pink-500 mb-2"></i>
                  <p className="font-semibold">週1日・1時間から勤務可能</p>
                </div>
                <div className="p-4">
                  <i className="ri-briefcase-line text-3xl text-blue-500 mb-2"></i>
                  <p className="font-semibold">WワークOK</p>
                </div>
              </div>
              <p className="text-lg text-gray-600 mt-6">
                働きやすい環境で地域を支える仕事です。
              </p>
            </div>
            <div className="text-center">
              <Link 
                to="/careers" 
                className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                採用情報を見る
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">株式会社歩</h3>
              <div className="space-y-2 text-gray-300">
                <p>〒212-0023 神奈川県川崎市幸区戸手本町1-123-12 ドルフ5 203</p>
                <p>Tel：044-280-7266</p>
                <p>Fax：044-280-7267</p>
                <p>介護保険事業所番号：1475102685</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">会社情報</h3>
              <div className="space-y-2 text-gray-300">
                <p>設立：平成29年2月1日</p>
                <p>資本金：200万円</p>
                <p>代表取締役：吉田 香</p>
                <p>管理者：菊池 雪野</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">© 2025 訪問介護ゆきのケア All Rights Reserved.</p>
            <a href="https://readdy.ai/?origin=logo" className="text-blue-400 hover:text-blue-300 ml-4">Website Builder</a>
          </div>
        </div>
      </footer>

      {/* 固定お問い合わせボタン */}
      <div className="fixed bottom-6 right-6 z-50">
        <a 
          href="https://ws.formzu.net/dist/S634209674/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full shadow-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center"
        >
          <i className="ri-phone-line mr-2"></i>
          お問い合わせはこちら
        </a>
      </div>
    </div>
  );
}
